import React, { useContext, useState, SyntheticEvent } from 'react';
import styled from 'styled-components';
import { THit } from '@com/core/common/types/catalog';
import LazyLoad from 'react-lazyload';
import MediaPreloader from '@com/core/common/components/mediaPreloader';
import { getTransformationUrl } from '@com/core/common/utils/image';
import { IMG_BASE } from '@com/core/common/baseConfig';
import TagWrap from '@com/core/kul/components/tag/TagWrap';
import { CustomerContext } from '@com/core/common/contexts/customerContext';
import { CartContext } from '@com/core/common/contexts/cartContext';
import TagDiscount from '@com/core/kul/components/tagDiscount/TagDiscount';
import kulConfig from '@com/core/kul/kulConfig';
import Loader from '@com/core/kul/components/loader/Loader';
import { useTranslation } from '@com/core/common/translations/i18n';
import Price from '@com/core/kul/components/price/Price';
import { colors } from '@com/core/kul/styles/kulTheme';
import { AppContext } from '../../../common/contexts/appContext';

type TProps = {
    product: THit;
};

const commonSpace = 12;

const ProductBoxSlider = ({ product }: TProps): JSX.Element => {
    const { t } = useTranslation('common');
    const { locale } = useContext(AppContext);
    const { customer, isSignedIn } = useContext(CustomerContext);
    const { addToCart, addToWishlist, isAdded, isInWishlist, removeItem } = useContext(CartContext);
    const [isLoading, setLoading] = useState(false);

    const isSavedItem = isInWishlist(product.offer_code);
    const isAddedItem = isAdded(product.offer_code);
    const wishlistIcon = isSavedItem ? kulConfig.assets.icons.wishListAdded : kulConfig.assets.icons.wishList;

    const imageLoader = (): JSX.Element => {
        return (
            <MediaPreloader productImage>
                <img src={`${IMG_BASE}/kul/images/kul-product-image-default-${locale.lang}.png`} alt={product.name} />
            </MediaPreloader>
        );
    };

    const handleAddWishList = async (e: SyntheticEvent<HTMLElement>): Promise<void> => {
        e.stopPropagation();
        e.preventDefault();
        if (isSavedItem) {
            removeItem(product.offer_code, 'savedcart', product.sku_config);
            return;
        }
        addToWishlist(product.offer_code, product.sku_config);
    };

    const handleAddToCart = async (e: SyntheticEvent<HTMLElement>): Promise<void> => {
        e.stopPropagation();
        e.preventDefault();
        setLoading(true);
        await addToCart(product.offer_code, 1, false, [], product.sku_config);
        setLoading(false);
    };

    return (
        <Container>
            <div>
                <ImageContainer>
                    <div className="productImage">
                        <LazyLoad placeholder={imageLoader()} offset={300}>
                            <MediaPreloader productImage>
                                <img src={getTransformationUrl(product.image_key)} alt={product.name} />
                            </MediaPreloader>
                        </LazyLoad>
                    </div>
                    <ImageHeader>
                        <div className="tagContainer">
                            {product.catalog_tag ? (
                                <TagWrap bgColor={product.catalog_tag_type}>{product.catalog_tag}</TagWrap>
                            ) : (
                                <TagDiscount preReductionPrice={product.price} sellingPrice={product.sale_price} />
                            )}
                        </div>
                        {isSignedIn && (
                            <WishlistToggle
                                type="button"
                                className={`wishlistToggle${isSavedItem ? ' wishlistToggleActive' : null}`}
                                sWishlist={!!customer}
                                onClick={handleAddWishList}>
                                <span className="wishlistIconContainer">
                                    <img src={wishlistIcon} alt="wishlist" />
                                </span>
                            </WishlistToggle>
                        )}
                    </ImageHeader>
                    <AddToCart>
                        <AddToCartButton
                            type="button"
                            disabled={isAddedItem || isLoading}
                            onClick={handleAddToCart}
                            isAdded={isAddedItem}>
                            {isLoading ? (
                                <Loader type="bounce" color={colors.brandSecondary} />
                            ) : (
                                <>
                                    <img src={kulConfig.assets.icons.cartBrand} alt="Add To Cart" />
                                    <span>{t(`${isAddedItem ? 'added' : 'add'}`)}</span>
                                </>
                            )}
                        </AddToCartButton>
                    </AddToCart>
                </ImageContainer>
                <Title>{product.name}</Title>
            </div>
            <PriceCover>
                <Price price={product.price} salePrice={product.sale_price} size="large" decimals={2} />
            </PriceCover>
        </Container>
    );
};

const Container = styled.div`
    cursor: pointer;
    position: relative;
    border-radius: 9px;
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    padding: ${commonSpace}px;
    border: 1px solid ${({ theme }): string => theme.colors.bodyLight};
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const ImageContainer = styled.div`
    margin-bottom: ${commonSpace}px;
    position: relative;

    .productImage {
        border-radius: 6px;
        overflow: hidden;
    }
`;

const ImageHeader = styled.div`
    margin: -12px -12px 0;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .tagContainer {
        flex: 1;
        ${({ theme: { right } }): string => `margin-${right}: 4px`};
        margin-top: 16px;
    }
`;

const WishlistToggle = styled.button<{ sWishlist: boolean }>`
    visibility: ${({ sWishlist }): string => (sWishlist ? 'visible' : 'hidden')};
    pointer-events: ${({ sWishlist }): string => (sWishlist ? 'auto' : 'none')};
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    .wishlistIconContainer {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 100%;
        background-color: rgba(0, 0, 0, 0.1);

        img {
            width: 14px;
        }
    }

    &.wishlistToggleActive .wishlistIconContainer {
        background-color: ${({ theme }): string => theme.colors.brandPrimaryLight};
    }
`;

const AddToCart = styled.div`
    position: absolute;
    bottom: -4px;
    right: -4px;
    z-index: 2;
`;

const AddToCartButton = styled.button<{ isAdded: boolean }>`
    border-radius: 4px;
    padding: 0 7px;
    height: 36px;
    width: 116px;
    border: 2px solid
        ${({ isAdded, theme }): string => (isAdded ? theme.colors.ctaDisabledBg : theme.colors.brandSecondary)};
    background-color: ${({ isAdded, theme }): string =>
        isAdded ? theme.colors.ctaDisabledBg : theme.colors.bgPrimary};
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        width: 20px;
        ${({ theme: { right } }): string => `margin-${right}: 8px`};
        display: ${({ isAdded }): string => (isAdded ? 'none' : 'block')};
    }

    span {
        font-size: 14px; /* purposely not rem */
        color: ${({ isAdded, theme }): string =>
            isAdded ? theme.colors.ctaDisabledBody : theme.colors.brandSecondary};
        font-weight: bold;
        margin-top: 1px;
    }
`;

const Title = styled.div`
    font-size: 14px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PriceCover = styled.div`
    margin-top: ${commonSpace / 2}px;
`;
export default ProductBoxSlider;
